import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, MapPin, Clock } from 'lucide-react';
import BlogEmailCapture from './BlogEmailCapture';
import MidtownStudioCallout from './MidtownStudioCallout';

/**
 * Reusable neighborhood "back and neck pain massage" blog post.
 * Renders a full SEO/AEO/GEO-optimized article with dynamic neighborhood content.
 *
 * Required data prop fields:
 *  - neighborhood, slug, image, driveMinutes, driveNote (short travel description),
 *    routes (array of strings), nearbyAreas (array), pageTitle, hookLine
 */
const NeighborhoodPainPost = ({ data }) => {
  const {
    neighborhood,
    slug,
    image,
    driveMinutes,
    driveNote,
    routes,
    nearbyAreas,
    pageTitle,
    hookLine,
    dataTestId,
  } = data;

  useEffect(() => {
    const prev = document.title;
    document.title = pageTitle;
    return () => { document.title = prev; };
  }, [pageTitle]);

  const publishedDate = 'February 13, 2026';
  const isoDate = '2026-02-13';
  const canonicalUrl = `https://ayumisuzuki.com/blog/${slug}`;
  const headline = `Back and Neck Pain Massage in ${neighborhood}: A 2026 Local Guide`;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50"
      data-testid={dataTestId}
    >
      {/* Article JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline,
            description: `Looking for back and neck pain massage in ${neighborhood}? A 2026 local guide covering causes, what to expect, pricing, and how to book with certified therapist Ayumi Suzuki minutes away in midtown Sacramento.`,
            image,
            author: {
              '@type': 'Person',
              name: 'Ayumi Suzuki',
              url: 'https://ayumisuzuki.com/about',
              jobTitle: 'Certified Massage Therapist',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Ayumi Suzuki Massage Therapy',
              logo: {
                '@type': 'ImageObject',
                url: 'https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/hdysr3f5_ayumi1%20logo.jpg',
              },
            },
            datePublished: isoDate,
            dateModified: isoDate,
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
            articleSection: 'Pain Relief',
            keywords: `back and neck pain massage ${neighborhood}, ${neighborhood} massage therapist, back pain massage Sacramento, neck pain relief ${neighborhood}, Ayumi Suzuki`,
            about: [
              { '@type': 'Thing', name: 'Back and Neck Pain Massage' },
              { '@type': 'Place', name: `${neighborhood}, Sacramento, California` },
            ],
            inLanguage: 'en-US',
            isAccessibleForFree: true,
          }),
        }}
      />

      {/* Service JSON-LD - GEO signal */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Back and Neck Pain Massage Therapy',
            provider: {
              '@type': 'LocalBusiness',
              name: 'Ayumi Suzuki Massage Therapy',
              telephone: '+14156789133',
              email: 'ayumisuzuki@yahoo.com',
              url: 'https://ayumisuzuki.com',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '2528 I Street',
                addressLocality: 'Sacramento',
                addressRegion: 'CA',
                postalCode: '95816',
                addressCountry: 'US',
              },
              geo: { '@type': 'GeoCoordinates', latitude: '38.5734', longitude: '-121.4785' },
              priceRange: '$$',
            },
            areaServed: [
              { '@type': 'Place', name: `${neighborhood}, Sacramento, California` },
              ...nearbyAreas.map((n) => ({ '@type': 'Place', name: `${n}, California` })),
            ],
            availableChannel: {
              '@type': 'ServiceChannel',
              serviceUrl: 'https://ayumisuzuki.com/book',
            },
          }),
        }}
      />

      {/* FAQ JSON-LD for AEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `Where can I get back and neck pain massage in ${neighborhood}?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `The closest certified massage therapist offering back and neck pain relief for ${neighborhood} residents is Ayumi Suzuki at 2528 I Street, Sacramento, CA 95816 — roughly ${driveMinutes} from most of ${neighborhood}. Book online at ayumisuzuki.com or text 415-678-9133.`,
                },
              },
              {
                '@type': 'Question',
                name: 'Does massage really help with chronic back and neck pain?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Yes. Skilled therapeutic and deep tissue massage reduces muscle tension, improves range of motion, and calms the nervous-system responses that keep pain patterns locked in. For chronic back and neck pain, consistent sessions (weekly to bi-weekly) for the first 4–6 weeks tend to produce the most noticeable improvement.`,
                },
              },
              {
                '@type': 'Question',
                name: `How much does back and neck pain massage cost near ${neighborhood}?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `At Ayumi Suzuki's midtown Sacramento practice: 30 minutes $55, 45 minutes $70, 60 minutes $110, 90 minutes $160, 120 minutes $200. Pain-focused sessions are personalized to your body and history.`,
                },
              },
              {
                '@type': 'Question',
                name: 'What type of massage is best for back and neck pain?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `A blend of therapeutic and deep tissue massage tends to work best. Deep tissue reaches the muscle layers that hold chronic tension; therapeutic work adapts pressure and technique to your nervous system's response. For most back and neck pain, the goal is not just release — it's a session tailored to your specific pattern.`,
                },
              },
              {
                '@type': 'Question',
                name: 'How soon can I expect back and neck pain relief?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Many clients feel meaningful relief the same day. For chronic back or neck pain, the most durable improvements — better sleep, mobility, fewer flare-ups — usually appear within 3–4 consistent sessions.`,
                },
              },
              {
                '@type': 'Question',
                name: `Is it easy to drive from ${neighborhood} to midtown for a session?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Yes — ${driveNote}. Street parking is generally available within a short walk of 2528 I Street.`,
                },
              },
            ],
          }),
        }}
      />

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group mb-4"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Blog</span>
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="flex items-center" data-testid="post-date">
              <Calendar className="w-4 h-4 mr-2" />
              <time dateTime={isoDate}>{publishedDate}</time>
            </span>
            <span className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              Ayumi Suzuki
            </span>
            <span className="flex items-center">
              <MapPin className="w-4 h-4 mr-2" />
              {neighborhood} &amp; Midtown
            </span>
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              10 min read
            </span>
          </div>

          <h1
            className="text-4xl md:text-5xl font-serif text-gray-800 mb-6 leading-tight"
            data-testid="post-title"
          >
            Back and Neck Pain Massage in {neighborhood}
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed">
            {hookLine} This 2026 guide covers where to find certified back and neck pain massage near {neighborhood}, what causes chronic tension in that area, what a session looks like, pricing, and how to book with certified midtown Sacramento therapist Ayumi Suzuki.
          </p>
        </header>

        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img
            src={image}
            alt={`Back and neck pain massage therapy near ${neighborhood} Sacramento`}
            className="w-full h-96 object-cover"
          />
        </div>

        {/* TL;DR */}
        <div className="bg-white border border-teal-100 rounded-2xl p-6 mb-12 shadow-sm">
          <h2 className="text-xl font-serif text-teal-700 mb-2">Quick Answer (TL;DR)</h2>
          <p className="text-gray-700 leading-relaxed">
            For {neighborhood} residents dealing with chronic back or neck pain, the closest certified massage therapist is <strong>Ayumi Suzuki</strong> — about {driveMinutes} from most of the neighborhood, at <strong>2528 I Street, Sacramento, CA 95816</strong>. Sessions blend deep tissue and therapeutic techniques (30–120 min · $55–$200). Book online or text <a href="sms:+14156789133" className="text-teal-600 hover:underline">415-678-9133</a>.
          </p>
        </div>

        <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-a:text-teal-700">
          <h2>Why Back and Neck Pain Is So Common in {neighborhood}</h2>
          <p>
            Chronic back and neck pain has become one of the most common wellness complaints across every Sacramento neighborhood — and {neighborhood} is no exception. Long hours at a desk, driving, carrying kids and gear, sleeping on the wrong pillow, and the constant micro-tension of everyday life all quietly compound into muscle patterns that show up as pain.
          </p>
          <p>
            The good news: back and neck pain from muscular tension usually responds very well to skilled, consistent massage therapy. And the closest concentration of certified therapists to {neighborhood} sits just minutes away in midtown Sacramento.
          </p>

          <h2>The Most Common Sources of Back and Neck Pain We See</h2>
          <ul>
            <li><strong>Desk and screen posture</strong> — forward head, rounded shoulders, upper-back "tech neck"</li>
            <li><strong>Driving and commuting</strong> — locked shoulders and low-back compression</li>
            <li><strong>Parenting and caregiving</strong> — repetitive lifting, carrying, side-loading</li>
            <li><strong>Sleep issues</strong> — pillow height, side sleeping, and stress-related jaw clenching</li>
            <li><strong>Old injuries flaring up</strong> — falls, whiplash, or muscle strains that never fully resolved</li>
            <li><strong>Chronic stress</strong> — the nervous system keeps the upper back and neck braced in a low-grade "guard" position</li>
          </ul>

          <h2>How Massage Actually Helps Back and Neck Pain</h2>
          <p>
            A skilled therapeutic and deep tissue session addresses back and neck pain at three levels:
          </p>
          <ol>
            <li><strong>Muscle release.</strong> Deep, sustained pressure releases the tight fibers and adhesions holding tension in place.</li>
            <li><strong>Postural reset.</strong> Working the front (chest, hip flexors) and back (traps, lats, glutes) together helps the body return to a more balanced alignment.</li>
            <li><strong>Nervous-system calming.</strong> Chronic pain is often reinforced by an over-activated stress response. A calm, focused session helps down-regulate that response — where a lot of long-term pain actually lives.</li>
          </ol>

          <h2>Where {neighborhood} Residents Go for Back and Neck Pain Massage</h2>
          <p>
            The closest certified massage therapist to {neighborhood} is Ayumi Suzuki, whose studio at 2528 I Street sits in the heart of midtown Sacramento. From most of {neighborhood} it's roughly {driveMinutes} — realistic enough to build regular sessions into your week when you're actively working through a pain pattern.
          </p>

          <h3>Getting there</h3>
          <ul>
            {routes.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
            <li><strong>Parking:</strong> Street parking generally available within a short walk of the studio</li>
          </ul>

          <h2>What a Back and Neck Pain Session Actually Looks Like</h2>
          <h3>Before the session</h3>
          <p>
            You'll fill out a short intake covering when the pain started, what makes it worse, sleep patterns, and any medical history. Hydration the day of and skipping heavy meals in the hour beforehand make the session more comfortable.
          </p>
          <h3>During the session</h3>
          <p>
            The room is warm and calm. Work typically focuses on the neck, shoulders, upper back, and traps first — where most tension for {neighborhood} desk workers, drivers, and caregivers lives — then adapts based on what your body reveals. Pressure adjusts in real time to what your nervous system can take.
          </p>
          <h3>After the session</h3>
          <p>
            Most clients feel meaningful relief the same day, with mild soreness possible the next 24–48 hours (similar to a good workout). Improvements in sleep, mobility, and pain levels typically continue for several days.
          </p>

          <h2>How Often to Book (Pain-Focused Cadence)</h2>
          <ul>
            <li><strong>Acute or high pain:</strong> weekly for 4–6 weeks</li>
            <li><strong>Moderate chronic pain:</strong> every 10–14 days for the first 2 months</li>
            <li><strong>Maintenance after relief:</strong> every 3–4 weeks</li>
            <li><strong>Flare-up prevention:</strong> monthly minimum, especially through stressful seasons</li>
          </ul>

          <h2>Pricing</h2>
          <ul>
            <li><strong>30 minutes — $55</strong> · targeted neck or upper-back relief</li>
            <li><strong>45 minutes — $70</strong> · focused therapeutic work</li>
            <li><strong>60 minutes — $110</strong> · full back &amp; neck session</li>
            <li><strong>90 minutes — $160</strong> · extended pain-focused restoration</li>
            <li><strong>120 minutes — $200</strong> · deep recovery &amp; reset</li>
          </ul>

          <h2>How to Book</h2>
          <ul>
            <li><strong>Book online</strong> via the live <Link to="/book" className="text-teal-600 hover:underline">appointment scheduler</Link></li>
            <li><strong>Text:</strong> <a href="sms:+14156789133" className="text-teal-600 hover:underline">415-678-9133</a></li>
            <li><strong>Email:</strong> <a href="mailto:ayumisuzuki@yahoo.com" className="text-teal-600 hover:underline">ayumisuzuki@yahoo.com</a></li>
            <li><strong>Address:</strong> 2528 I Street, Sacramento, CA 95816 ({driveMinutes} from {neighborhood})</li>
          </ul>

          <h2>Frequently Asked Questions</h2>

          <h3>Will I need multiple sessions to feel a real difference?</h3>
          <p>For acute pain, often just one session brings noticeable relief. For chronic patterns, expect meaningful, lasting improvement over 3–4 sessions.</p>

          <h3>Is deep tissue too intense for back and neck pain?</h3>
          <p>Not when done well. Deep tissue for pain is slow, focused, and always calibrated to your feedback. The goal is release, not intensity.</p>

          <h3>Can I combine massage with chiropractic or physical therapy?</h3>
          <p>Yes — many clients do. Massage often makes the other work "stick" better by keeping the surrounding muscles from pulling structures back out of alignment.</p>

          <h3>Do you offer gift certificates?</h3>
          <p>Yes. A pain-focused session is a genuinely useful gift for anyone in your life struggling with tension or chronic discomfort.</p>

          {/* CTA */}
          <div className="not-prose bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white mt-12">
            <h3 className="text-2xl font-serif mb-4">
              Book Back and Neck Pain Massage for {neighborhood} Residents
            </h3>
            <p className="mb-6">
              Personalized, pain-focused deep tissue and therapeutic massage at 2528 I Street — {driveMinutes} from most of {neighborhood}. Reserve a session and start working through the tension patterns your body has been carrying.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/book"
                className="inline-block bg-white text-teal-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-300 text-center"
                data-testid="cta-book-now"
              >
                Book Your Session
              </Link>
              <Link
                to="/about"
                className="inline-block bg-teal-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-900 transition-colors duration-300 text-center"
                data-testid="cta-about"
              >
                Meet Ayumi
              </Link>
            </div>
          </div>
        </div>

        <MidtownStudioCallout />
        <BlogEmailCapture source={`pain-${slug}`} />

        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-start space-x-6">
            <img
              src="https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/aautielc_Ayumi.jpg"
              alt="Ayumi Suzuki — Certified Massage Therapist"
              className="w-24 h-24 rounded-full object-cover"
            />
            <div>
              <h3 className="text-2xl font-serif text-gray-800 mb-2">About Ayumi Suzuki</h3>
              <p className="text-gray-600 mb-4">
                Ayumi Suzuki is a California CAMTC-certified massage therapist practicing at 2528 I Street in midtown Sacramento — a short drive from {neighborhood}. Her personalized sessions blend therapeutic, deep tissue, and Swedish techniques to address chronic back and neck pain at the muscle, postural, and nervous-system level.
              </p>
              <Link to="/about" className="text-teal-600 hover:text-teal-700 font-medium">
                Learn more about Ayumi's approach →
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default NeighborhoodPainPost;
